/**
 * i18n Configuration
 * UI strings are loaded from bundled JSON under ./locales (no translation API).
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { getLanguageFromUrl } from "../utils/hreflangGenerator";
import { store } from "../store/configureStore";
import { setCurrentLanguage } from "../store/actions/translationActions";

// Static locale files bundled at build time
import en from "./locales/en.json";
import enGB from "./locales/en-GB.json";
import enAU from "./locales/en-AU.json";
import ga from "./locales/ga.json";
import de from "./locales/de.json";
import deAT from "./locales/de-AT.json";
import es from "./locales/es.json";
import it from "./locales/it.json";
import no from "./locales/no.json";
import fi from "./locales/fi.json";
import da from "./locales/da.json";
import sv from "./locales/sv.json";
import fr from "./locales/fr.json";
import pt from "./locales/pt.json";
import nl from "./locales/nl.json";

const SUPPORTED_LANGS = [
  "en",
  "en-GB",
  "en-AU",
  "ga",
  "de",
  "de-AT",
  "es",
  "it",
  "no",
  "fi",
  "da",
  "sv",
  "fr",
  "pt",
  "nl",
];

const defaultLanguage = "en";

const staticResources = {
  en: { translation: en },
  "en-GB": { translation: enGB },
  "en-AU": { translation: enAU },
  ga: { translation: ga },
  de: { translation: de },
  "de-AT": { translation: deAT },
  es: { translation: es },
  it: { translation: it },
  no: { translation: no },
  fi: { translation: fi },
  da: { translation: da },
  sv: { translation: sv },
  fr: { translation: fr },
  pt: { translation: pt },
  nl: { translation: nl },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    defaultNS: "translation",
    fallbackLng: defaultLanguage,
    interpolation: { escapeValue: false },
    detection: {
      order: ["path", "localStorage", "navigator"],
      lookupFromPathIndex: 0,
      caches: ["localStorage"],
      checkWhitelist: true,
    },
    supportedLngs: SUPPORTED_LANGS,
    resources: staticResources,
    react: { useSuspense: false },
    debug: process.env.NODE_ENV === "development",
  });

export const changeLanguage = async (lang) => {
  try {
    const langMap = { "en-GB": "en", "en-AU": "en", "de-AT": "de" };
    const baseLang = langMap[lang] || lang;
    const validLang = SUPPORTED_LANGS.includes(lang)
      ? lang
      : SUPPORTED_LANGS.includes(baseLang)
        ? baseLang
        : "en";

    await i18n.changeLanguage(validLang);
    store.dispatch(setCurrentLanguage(lang));

    if (typeof document !== "undefined") {
      document.documentElement.lang = validLang;
    }
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("preferredLanguage", lang);
    }

    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error(`Error changing language to ${lang}:`, error);
    }
    return false;
  }
};

export const initializeI18n = async () => {
  try {
    const lang = getLanguageFromUrl() || defaultLanguage;
    await changeLanguage(lang);
    return lang;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("Error initializing i18n:", error);
    }
    await i18n.changeLanguage(defaultLanguage);
    return defaultLanguage;
  }
};

export default i18n;
